import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

export interface ColorPalette {
  simple_menu: {
    page_background: string;
    menu_background: string;
    item_background: string;
  };
  book_menu: {
    page_background: string;
    menu_background: string;
    item_background: string;
    book_background: string;
  };
  web_menu: {
    page_background: string;
    item_background: string;
  };
}

export interface Slider {
  image_url?: string;
  description?: string;
  description_ar?: string;
}

export interface Service {
  index: number;
  name: string;
  description: string;
  icon_url?: string;
}

export interface Business {
  id: number;
  name: string;
  name_ar: string;
  description?: string;
  description_ar?: string;
  logo?: string;
  favicon?: string;
  email: string;
  color_palette: ColorPalette;
  template_id?: string | null;
  sliders?: Slider[];
  services?: Service[];
  services_ar?: Service[];
  about_us?: string;
  about_us_ar?: string;
  postal_address?: string;
  postal_address_ar?: string;
  office_numbers?: string[];
  social_urls?: {
    facebook?: string;
    instagram?: string;
    behance?: string;
    linkedin?: string;
    tiktok?: string;
    youtube?: string;
    twitter?: string;
    whatsapp?: string;
    google_reviews?: string;
    deliveries?: string[];
    work_hours?: string;
    support_email?: string;
  };
  menu_id?: string;
}

export interface ResolvedMenu {
  type: 'branch' | 'chain';
  branch_id?: number;
  chain_id?: number;
  branch_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class BusinessService {
  private apiUrl = environment.apiUrl;
  private cache = new Map<string, { data: any; ts: number }>();
  private readonly TTL = 5 * 60 * 1000; // 5 minutes

  constructor(private http: HttpClient) {}

  private getCached<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.ts > this.TTL) { this.cache.delete(key); return null; }
    return entry.data as T;
  }

  private setCached(key: string, data: any): void {
    this.cache.set(key, { data, ts: Date.now() });
  }

  getBusiness(id: number): Observable<Business> {
    const key = `biz_${id}`;
    const cached = this.getCached<Business>(key);
    if (cached) return of(cached);
    return this.http.get<Business>(`${this.apiUrl}/business/${id}`)
      .pipe(tap(data => this.setCached(key, data)));
  }

  getLandingPage(id: number): Observable<Business> {
    const key = `landing_${id}`;
    const cached = this.getCached<Business>(key);
    if (cached) return of(cached);
    return this.http.get<Business>(`${this.apiUrl}/landing-page/${id}`)
      .pipe(tap(data => this.setCached(key, data)));
  }

  resolveMenu(id: number | string): Observable<ResolvedMenu> {
    const key = `resolve_${id}`;
    const cached = this.getCached<ResolvedMenu>(key);
    if (cached) return of(cached);
    return this.http.get<ResolvedMenu>(`${this.apiUrl}/resolve-menu/${id}`)
      .pipe(tap(data => this.setCached(key, data)));
  }
}
